<template>
  <section class="page">
    <div class="page__header">
      <h2 class="page__title">{{ $t('kps.guardRules.title') }}</h2>
    </div>

    <div class="section-card">
      <DataTable
          :columns="visibleColumns"
          :rows="rows"
          :rowKey="rowKey"
          :headerHeight="56"
          :rowHeight="64"
      >
        <template #cell-idx="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.idx }}</div>
          <input v-else class="tbl-input" v-model="row.idx" />
        </template>

        <template #cell-dateTime="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.dateTime }}</div>
          <input 
            v-else 
            :value="formatToDateTimeInput(row.dateTime)" 
            @input="row.dateTime = formatFromDateTimeInput($event.target.value)"
            type="datetime-local" 
            class="tbl-input" 
          />
        </template>

        <template #cell-place="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.place }}</div>
          <input v-else class="tbl-input" v-model="row.place" />
        </template>

        <template #cell-who="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.who }}</div>
          <SelectField v-else v-model="row.violator_id" :options="users" :placeholder="$t('common.chooseOption')" />
        </template>

        <template #cell-nature="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.nature }}</div>
          <input v-else class="tbl-input" v-model="row.nature" />
        </template>

        <template #cell-reported="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.reported }}</div>
          <SelectField v-else v-model="row.reported_to_id" :options="users" :placeholder="$t('common.chooseOption')" />
        </template>

        <template #cell-measures="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.measures }}</div>
          <input v-else class="tbl-input" v-model="row.measures" />
        </template>

        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>

        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">
            <button
                v-if="!row.editing"
                class="actions__btn actions__btn--edit"
                @click="onEdit(row)"
                :title="$t('common.edit')"
                :aria-label="$t('common.edit')"
            >
              <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
            </button>
            <template v-else>
              <button
                  class="actions__btn actions__btn--accept"
                  @click="onAccept(row)"
                  :title="$t('common.save')"
                  :aria-label="$t('common.save')"
              >
                <img class="actions__icon" src="../../assets/images/accept.svg" alt="accept" />
              </button>
              <button
                  class="actions__btn actions__btn--decline"
                  @click="onDecline(row)"
                  :title="$t('common.cancel')"
                  :aria-label="$t('common.cancel')"
              >
                <img class="actions__icon" src="../../assets/images/decline.svg" alt="decline" />
              </button>
            </template>
            <button
                class="actions__btn actions__btn--delete"
                @click="onDelete(row)"
                :title="$t('common.delete')"
                :aria-label="$t('common.delete')"
            >
              <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
            </button>
          </div>
        </template>
      </DataTable>

      <div style="margin-top: 8px; display: flex; justify-content: flex-end; padding: 0 12px 12px;">
        <ActionButton v-if="!readOnly" :color="'#2A8A1B'" :text="$t('kps.guardRules.addRecord')" @click="addRow" />
      </div>
    </div>

    <div class="page__footer">
      <ViewNavButtons
          :prev-text="$t('common.back')"
          :next-text="$t('common.next')"
          :prev-to="{ path: '/kps/border-regime-violations' }"
          :next-to="null"
      />
    </div>

    <transition name="toast-slide">
      <div v-if="showToast" class="toast" :class="toastMessage.includes('✗') ? 'toast-error' : 'toast-success'">
        <div class="toast-content">{{ toastMessage }}</div>
      </div>
    </transition>
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue'
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import SelectField from '@/components/widgets/SelectField.vue'
import {
  updateRulesViolation,
  createRulesViolation,
  deleteRulesViolation,
} from '@/api/rulesViolations'
import { checkOrCreateBookByDate } from '@/api/book'
import { http } from '@/api/http'

import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import { canEdit } from '@/access/armAccess'

export default {
  name: 'KpsGuardRulesViolationsView',
  components: { DataTable, ViewNavButtons, SelectField , ActionButton },
  data() {
    return {
      currentUser: null,
      rows: [],
      bookId: null,
      bookContext: null,
      users: [],

      toastMessage: '',
      showToast: false,
    }
  },
  computed: {
    canEditKps() {
      return canEdit(this.currentUser || getCachedCurrentUser(), '/kps/section-4')
    },
    readOnly() { return !this.canEditKps },
    columns() {
      return [
        { key: 'idx', label: this.$t('kps.guardRules.colIdx'), width: 73 },
        { key: 'dateTime', label: this.$t('kps.guardRules.colDateTime'), width: 220 },
        { key: 'place', label: this.$t('kps.guardRules.colPlace'), width: 146 },
        { key: 'who', label: this.$t('kps.guardRules.colWho'), width: 146 },
        { key: 'nature', label: this.$t('kps.guardRules.colNature'), width: 200 },
        { key: 'reported', label: this.$t('kps.guardRules.colReported'), width: 180 },
        { key: 'measures', label: this.$t('kps.guardRules.colMeasures'), width: 160 },
        { key: 'actions', label: '', width: 150 },
      ]
    },
    visibleColumns() {
      return this.readOnly ? this.columns.filter(c => c.key !== 'actions') : this.columns;
    },
  },
  async mounted() {
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
    this.initAndFetch()
  },
  methods: {
    rowKey(r) {
      return r.__id ?? r.id
    },

    showSuccessToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 2500)
    },
    showErrorToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 3500)
    },
    validationMessageFromError(e) {
      if (!e || e.status !== 400 || !e.payload || typeof e.payload !== 'object') return ''
      const labels = { item_number: this.$t('kps.guardRules.reqIdx'), datetime: this.$t('kps.guardRules.reqDateTime') }
      const missing = []
      for (const [k, v] of Object.entries(e.payload)) {
        const msg = Array.isArray(v) ? v.join(' ') : String(v || '')
        if (/required|обязател|blank|пуст/i.test(msg)) missing.push(labels[k] || k)
      }
      if (missing.length) return `${this.$t('common.fillRequiredMany')}: ${missing.join(', ')}`
      return ''
    },

    async initAndFetch() {
      try {
        const d = new Date()
        const two = (n) => (n < 10 ? `0${n}` : String(n))
        const dateISO = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`

        const bookCtx = await checkOrCreateBookByDate(dateISO)
        this.bookContext = bookCtx
        this.bookId = bookCtx?.id || bookCtx?.book?.id
        
        await this.fetchRows()
        await this.fetchUsers()
      } catch (e) {
        console.error('GuardRules: init error', e)
      }
    },

    async ensureBookId() {
      return this.bookId
    },

    async fetchRows() {
      if (!this.bookId) return
      try {
        // Берем rules_violations из контекста
        const list = this.bookContext?.rules_violations || []
        if (Array.isArray(list)) {
          this.rows = list.map((it) => {
            const rawDateTime = it.datetime ?? it.date_time ?? it.dateTime ?? ''
            const displayDateTime = this.formatIsoToDisplayDateTime(rawDateTime)

            return {
              id: it.id,
              idx: it.item_number ?? it.idx ?? '',
              dateTime: displayDateTime,
              place: it.place ?? '',
              who: (it.violator && typeof it.violator === 'object') ? (it.violator.full_name || '') : (it.violator ?? it.violator_name ?? it.who ?? ''),
              violator_id: it.violator_id ?? ((it.violator && typeof it.violator === 'object') ? it.violator.id : null),
              nature: it.condition ?? it.nature ?? '',
              reported: (it.reported_to && typeof it.reported_to === 'object') ? (it.reported_to.full_name || '') : (it.reported_to ?? it.reported_to_name ?? it.reported ?? ''),
              reported_to_id: it.reported_to_id ?? ((it.reported_to && typeof it.reported_to === 'object') ? it.reported_to.id : null),
              measures: it.measures_taken ?? it.measures ?? '',
              editing: false,
            }
          })
        }
      } catch (e) {
        console.error('GuardRules: list failed', e)
      }
    },

    addRow() {
      const row = {
        __id: 'grv-' + Date.now(),
        idx: '',
        dateTime: '',
        place: '',
        who: '',
        violator_id: null,
        nature: '',
        reported: '',
        reported_to_id: null,
        measures: '',
        editing: true,
      }
      this.rows = [...this.rows, row]
    },

    onEdit(row) {
      row.__backup = JSON.parse(JSON.stringify(row))
      row.editing = true
    },

    async onAccept(row) {
      try {
        if (!String(row.idx || '').trim()) {
          this.showErrorToast(`${this.$t('common.fillRequired')}: ${this.$t('kps.guardRules.reqIdx')}`)
          return
        }
        const { __id, __backup, editing, ...rest } = row
        const payload = this.buildPayload(row)

        if (row.id) {
          await updateRulesViolation(row.id, payload)
        } else {
          const bid = await this.ensureBookId()
          if (!bid) {
            this.showErrorToast(this.$t('common.bookUnresolvedShort'))
            return
          }
          const resp = await createRulesViolation({
            book: bid,
            type: 'guard-rules',
            ...payload,
          })
          if (resp && resp.id) row.id = resp.id
        }

        row.editing = false
        delete row.__backup

        // Update user display names after save
        if (row.violator_id) {
          const user = this.users.find(u => u.value === row.violator_id)
          if (user) row.who = user.label
        }
        if (row.reported_to_id) {
          const user = this.users.find(u => u.value === row.reported_to_id)
          if (user) row.reported = user.label
        }

        this.showSuccessToast(this.$t('common.saved'))

        void rest
      } catch (e) {
        console.error('GuardRules: save failed', e)
        this.showErrorToast(this.validationMessageFromError(e) || (this.$t('common.saveFailed') + ': ' + (e?.message || e)))
      }
    },

    onDecline(row) {
      if (row.__backup) {
        const b = row.__backup
        Object.keys(b).forEach((k) => {
          if (k !== '__backup') row[k] = b[k]
        })
        delete row.__backup
      }
      row.editing = false
    },

    async onDelete(row) {
      const id = row.__id ?? row.id
      if (!row.id) {
        const idx = this.rows.findIndex((r) => (r.__id ?? r.id) === id)
        if (idx !== -1) this.rows = [...this.rows.slice(0, idx), ...this.rows.slice(idx + 1)]
        return
      }
      try {
        await deleteRulesViolation(row.id)
        const idx = this.rows.findIndex((r) => (r.__id ?? r.id) === id)
        if (idx !== -1) this.rows = [...this.rows.slice(0, idx), ...this.rows.slice(idx + 1)]
        this.showSuccessToast(this.$t('common.deleted'))
      } catch (e) {
        console.error('GuardRules: delete failed', e)
        this.showErrorToast(this.$t('common.deleteFailed') + ': ' + (e?.message || e))
      }
    },

    async fetchUsers() {
      try {
        const response = await http.get('users/')
        const list = response.data
        if (Array.isArray(list)) {
          this.users = list.map(u => ({
            value: u.id,
            label: u.full_name,
            ...u
          }))
        }
      } catch (e) {
        console.error('Users: list failed', e)
      }
    },

    formatIsoToDisplayDateTime(dateTimeStr) {
      if (!dateTimeStr) return ''
      const m = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})/)
      if (!m) return dateTimeStr
      const [, y, mo, d, hhRaw, mi] = m
      const hh = hhRaw.padStart(2, '0')
      return `${d}.${mo}.${y} ${hh}:${mi}`
    },

    formatDisplayToIsoDateTime(dateTimeStr) {
      if (!dateTimeStr) return ''
      const m = dateTimeStr.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/)
      if (!m) return dateTimeStr
      const [, d, mo, y, hh, mi] = m
      return `${y}-${mo}-${d}T${hh}:${mi}:00`
    },
    formatToDateTimeInput(displayStr) {
      // Конвертирует "ДД.ММ.ГГГГ ЧЧ:ММ" -> "ГГГГ-ММ-ДДTЧЧ:ММ"
      if (!displayStr) return '';
      const m = displayStr.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/);
      if (!m) return displayStr;
      const [, d, mo, y, hh, mi] = m;
      return `${y}-${mo}-${d}T${hh}:${mi}`;
    },
    formatFromDateTimeInput(isoStr) {
      // Конвертирует "ГГГГ-ММ-ДДTЧЧ:ММ" -> "ДД.ММ.ГГГГ ЧЧ:ММ"
      if (!isoStr) return '';
      const m = isoStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (!m) return isoStr;
      const [, y, mo, d, hh, mi] = m;
      return `${d}.${mo}.${y} ${hh}:${mi}`;
    },
    buildPayload(row) {
      const isoDateTime = this.formatDisplayToIsoDateTime(row.dateTime)
      return {
        item_number: row.idx || '',
        datetime: isoDateTime || null,
        place: row.place || '',
        violator_id: row.violator_id ?? null,
        condition: row.nature || '',
        reported_to_id: row.reported_to_id ?? null,
        measures_taken: row.measures || '',
      }
    },
  },
}
</script>

<style scoped>
.page {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.page__title {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 31.2px;
}

.section-card {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 0;
}

.page__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}

/* Минимальные стили для CRUD */
.cell-text {
  flex: 1 1 0;
  color: #111827;
  font-size: 16px;
  line-height: 20px;
  width: 100%;  
}
.tbl-input {
  width: 100%;
  height: 32px;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  line-height: 20px;
  font-family: 'Times New Roman', Times, serif;
  color: #111827;
  background: #fff;
}
.tbl-input:focus {
  outline: 2px solid rgba(47, 120, 238, 0.25);
  outline-offset: 0;
}

.actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
  flex-wrap: wrap;
}
.actions__btn {
  width: 30px;
  height: 30px;
  padding: 6px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.actions__icon {
  width: 16px;
  height: 16px;
  display: block;
}
.actions__btn--edit {
  background: #2f78ee;
}
.actions__btn--accept {
  background: #2a8a1b;
}
.actions__btn--decline {
  background: #d81717;
}
.actions__btn--delete {
  background: #d81717;
}

/* Toast Notifications */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  min-width: 220px;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}
.toast-success { background: #2A8A1B; }
.toast-error { background: #D81717; }
.toast-content {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
}
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}
.toast-slide-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.toast-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
